import { tokenService, tokenUtils } from './tokenService'
import { AUTH_PARAMS } from './auth-utils'
import { createUserTokenMismatchError, createLoginFailedError, createSessionExpiredError, isAuthError, AuthErrorCodes } from './auth-errors'
import { userServiceEndpoints, lenderBackendEndpoints } from './auth-client.http'
import authLogger from './auth-logger'
import { SIGNIN_STEPS, signInErrorCode, trackSignInStep } from '@app/telemetry/embedding'

/**
 * Extract standard auth fields from an API response.
 * All login/token endpoints return: { access_token, expires, expires_at, expires_in }
 * @param {Object} response - Raw API response
 * @returns {{ accessToken: string, expiresAt: number|undefined, expiresIn: number|undefined }}
 */
function parseAuthResponse(response) {
  return {
    accessToken: response.access_token,
    expiresAt: response.expires_at ?? response.expires,
    expiresIn: response.expires_in,
  }
}

export class LoginResponse {
  accessToken
  expiresAt
  expiresIn

  constructor(accessToken, expiresAt, expiresIn) {
    this.accessToken = accessToken
    this.expiresAt = expiresAt
    this.expiresIn = expiresIn
  }
}

export class SessionResponse {
  accessToken
  expiresAt
  expiresIn

  constructor(accessToken, expiresAt, expiresIn) {
    this.accessToken = accessToken
    this.expiresAt = expiresAt
    this.expiresIn = expiresIn
  }
}

export class RedirectCallbackResponse {
  encompassLoanId
  reggoraOrigin
  redirectUrl
  accessToken
  expiresAt
  expiresIn

  constructor(accessToken, encompassLoanId, reggoraOrigin, redirectUrl, expiresAt, expiresIn) {
    this.accessToken = accessToken
    this.encompassLoanId = encompassLoanId
    this.reggoraOrigin = reggoraOrigin
    this.redirectUrl = redirectUrl
    this.expiresAt = expiresAt
    this.expiresIn = expiresIn
  }
}

/**
 * Auth client is meant to be used soley by the `AuthProvider`, not directly via other parts of the app.
 * It handles the core authentication operations such as login, logout, session checking, and user retrieval.
 * It is the only responsible party for setting and removing tokens from the `tokenService`.
 */
export class AuthClient {
  constructor() {
    this.currentUser = null
  }

  /**
   * checkSession verifies if the current session is valid, typically used on app load.
   * If sessions are enforced, a session must exist.
   * If sessions are not enforced, a bearer token will be validated.
   * @returns {Promise<undefined>}
   */
  checkSession = async () => {
    authLogger.debug('checking session')
    try {
      // In the short / medium term, we will check status with both the existing bearer token and the session cookie.
      // This is to support both flows while we transition fully to sessions.
      const mountedBearerToken = tokenService.getToken()
      const { accessToken, expiresIn } =
        parseAuthResponse(await userServiceEndpoints.STATUS(mountedBearerToken))
      tokenService.setToken(accessToken)
      trackSignInStep(SIGNIN_STEPS.SESSION_CHECK, { outcome: 'session_found' })
      return { expiresIn }
    } catch (error) {
      // We only need to be concerned about clearing the bearer token out since the session lifecycle is managed by the backend.
      tokenService.removeToken()
      trackSignInStep(SIGNIN_STEPS.SESSION_CHECK, { outcome: 'no_session', error_code: signInErrorCode(error) })

      if (isAuthError(error)) {
        // If its already an AuthError, just rethrow it
        if ([AuthErrorCodes.LOGIN_FAILED].includes(error.code)) {
          // Login failed is expected so don't log an error
          throw error
        }

        authLogger.error('check session failed:', { authErrorCode: error.code }, error)
        throw error
      }

      if (error.status === 401 || error.status === 403) {
        authLogger.debug('check session: not authenticated', { status: error.status })
      } else {
        authLogger.error('check session failed:', error)
      }
      throw createLoginFailedError()
    }
  }

  /**
   * getToken gets a new bearer token.
   * Requires a session to be active.
   * @returns {Promise<SessionResponse>}
   */
  getToken = async () => {
    authLogger.debug('get new token')

    try {
      const tokenResponse = await userServiceEndpoints.TOKEN()
      const { accessToken, expiresAt, expiresIn } = parseAuthResponse(tokenResponse)
      tokenService.setToken(accessToken)
      return new SessionResponse(accessToken, expiresAt, expiresIn)
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        authLogger.debug('token refresh failed: session expired', { status: error.status })
        tokenService.removeToken()
        throw createSessionExpiredError()
      }
      authLogger.error('token refresh failed:', error)
      throw error
    }
  }

  /**
   * Alias for getToken, used by tokenRefreshService.
   * @returns {Promise<SessionResponse>}
   */
  refreshToken = this.getToken

  getUser = async () => {
    const token = await tokenService.getTokenAsync()
    if (!token) {
      this.currentUser = null
      return this.currentUser
    }

    if (this.currentUser) {
      const tokenUser = tokenUtils.getUserIDFromToken(token)
      if (!tokenUser || tokenUser !== this.currentUser.id) {
        authLogger.warn('token user mismatch, clearing cached user')
        this.currentUser = null
        throw createUserTokenMismatchError()
      }

      return this.currentUser
    }

    try {
      this.currentUser = await lenderBackendEndpoints.CURRENT_USER(token)
    } catch (error) {
      const status = error.status || error.response?.status
      if (status === 401 || status === 403) {
        authLogger.debug('failed to fetch current user: not authenticated', { status })
      } else {
        authLogger.error('failed to fetch current user:', error)
      }
      this.currentUser = null
    }

    return this.currentUser
  }

  clearUser = async () => {
    this.currentUser = null
  }

  /**
   * Log in a user, verifying username and password combination for lender account
   * @param {username} username - username for password login
   * @param {password} password - password for password login
   * @param {lender_id} lender_id - lender account ID for login
   * @returns {Promise<LoginResponse>} - login response
   */
  login = async ({ username, password, lender_id }) => {
    authLogger.debug('login from credentials')
    let loginResult
    try {
      loginResult = await userServiceEndpoints.LOGIN(username, password, lender_id)
    } catch (error) {
      trackSignInStep(SIGNIN_STEPS.PASSWORD, { outcome: 'failed', error_code: signInErrorCode(error) })
      throw error
    }
    trackSignInStep(SIGNIN_STEPS.PASSWORD, { outcome: 'succeeded' })
    const { accessToken, expiresAt, expiresIn } = parseAuthResponse(loginResult)
    tokenService.setToken(accessToken)
    return new LoginResponse(accessToken, expiresAt, expiresIn)
  }

  /**
   * Logs a user in when loading the app with login redirect parameters
   * @returns {Promise<RedirectCallbackResponse>}
   */
  loginRedirectCallback = async () => {
    authLogger.debug('login from redirect')
    const currentSearchParams = new URLSearchParams(window.location.search)

    const loginCode = currentSearchParams.get(AUTH_PARAMS.LOGIN_CODE)
    const legacyAuthToken = currentSearchParams.get(AUTH_PARAMS.AUTH_TOKEN)

    if (!loginCode && !legacyAuthToken) {
      throw createLoginFailedError()
    }

    const method = loginCode ? 'code' : 'token'
    try {
      const response = await this.redeemRedirectCredential(currentSearchParams, loginCode, legacyAuthToken)
      trackSignInStep(SIGNIN_STEPS.REDIRECT_CALLBACK, { method, outcome: 'succeeded' })
      return response
    } catch (error) {
      trackSignInStep(SIGNIN_STEPS.REDIRECT_CALLBACK, { method, outcome: 'failed', error_code: signInErrorCode(error) })
      throw error
    }
  }

  /**
   * Redeems the login code or legacy token from the redirect parameters.
   * @returns {Promise<RedirectCallbackResponse>}
   */
  redeemRedirectCredential = async (currentSearchParams, loginCode, legacyAuthToken) => {
    let newAuthToken
    let expiresAt
    let expiresIn

    if (loginCode) {
      authLogger.debug('login with code')
      const parsed = parseAuthResponse(await userServiceEndpoints.LOGIN_CODE(loginCode))
      newAuthToken = parsed.accessToken
      expiresAt = parsed.expiresAt
      expiresIn = parsed.expiresIn
    }

    if (legacyAuthToken) {
      authLogger.debug('login with token')
      try {
        const parsed = parseAuthResponse(await userServiceEndpoints.LOGIN_TOKEN(legacyAuthToken))
        newAuthToken = parsed.accessToken
        expiresAt = parsed.expiresAt
        expiresIn = parsed.expiresIn
      } catch (error) {
        throw createLoginFailedError()
      }
    }

    const reggoraOrigin = currentSearchParams.get(AUTH_PARAMS.ORIGIN)
    const encompassLoanId = currentSearchParams.get(AUTH_PARAMS.ENCOMPASS_LOAN_ID)
    const redirectUrl = currentSearchParams.get(AUTH_PARAMS.REDIRECT_URL)
    const accessToken = newAuthToken ?? legacyAuthToken
    tokenService.setToken(accessToken)
    const response = new RedirectCallbackResponse(
      accessToken, encompassLoanId, reggoraOrigin, redirectUrl, expiresAt, expiresIn)
    return response
  }

  /**
   * @returns {Promise<void>}
   */
  logout = async () => {
    authLogger.debug('logging out')
    const currentBearerToken = tokenService.getToken()
    try {
      await userServiceEndpoints.LOGOUT(currentBearerToken)
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        authLogger.debug('logout: session already expired', { status: error.status })
      } else {
        authLogger.error('logout failed:', error)
      }
    } finally {
      // Clear cached user data on logout
      tokenService.removeToken()
      this.currentUser = null
      this.encompassLoanId = null
      this.reggoraOrigin = null
      this.redirectUrl = null
    }
  }
}
